import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { setProcess, setProcesses } from '../../slices/processSlice';
import { useAppSelector, useAppDispatch } from '../../hooks';

import { useAppContext } from '../../Context';

import { LoaderTemporal } from '../../components/shared/Loader';
import DataTable from '../../components/shared/DataTable';

import { Grid } from '@mui/material';

import { ColumnsType, OrderProcessType, ProcessType } from '../../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCheck, faEye, faForward, faPlay, faRoute, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Button } from '../../components/shared/FormElements';
import { setLoading, setPopAlert, setPopConfirmation } from '../../slices/miscSlice';

import { usePermissions, useProcessPermissions as checkProcessPermissions } from '../../services';
import { constants } from '../../constants';
import { store } from '../../store';

const ViewOrdersProcess: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiCall } = useAppContext();
  const dispatch = useAppDispatch();
  const process: ProcessType = useAppSelector((state) => state.process.process);
  const processes: Array<ProcessType> = useAppSelector((state) => state.process.processes);

  const {
    canChange_orderprocess,
    canView_orderprocess,
    canAdd_orderprocess,
    canAdd_reposition,
    canView_orders,
    canView_orders_all,
    canView_orders_own,
  } = usePermissions([
    'change_orderprocess',
    'view_orderprocess',
    'add_orderprocess',
    'add_reposition',
    'view_orders',
    'view_orders_all',
    'view_orders_own',
  ]);

  const [ordersProcess, setOrdersProcess] = useState<Array<OrderProcessType>>([]);

  const canViewProcess = (proc?: ProcessType | null) => {
    const slug = proc?.process_slug_permission;
    if (!slug) return true;
    return checkProcessPermissions(`view_${slug}`);
  };

  const getProcess = async (): Promise<ProcessType | null> => {
    dispatch(setLoading(true));
    const response = await apiCall('GET', `process/${id}/`, null, '', 'No se pudo obtener el proceso', true);
    dispatch(setLoading(false));
    if (response) {
      if (!canViewProcess(response)) {
        navigate('/procesos');
        return null;
      }
      dispatch(setProcess(response));
      return response;
    }
    return null;
  };

  const getProcesses = async () => {
    const response = await apiCall('GET', 'processes/', null, '', 'No se pudo obtener los procesos', true);
    if (response) {
      dispatch(setProcesses(response));
    }
  };

  const getOrdersProcess = async () => {
    const response = await apiCall(
      'GET',
      `order_process/process/${id}/`,
      null,
      '',
      'No se pudo obtener las ordenes del proceso',
      true
    );
    if (response) {
      setOrdersProcess(response);
    } else {
      setOrdersProcess([]);
    }
  };

  useEffect(() => {
    if (!canView_orderprocess) {
      navigate('/');
      return;
    }

    if (!id) {
      navigate('/procesos');
      return;
    }

    const processId = parseInt(id, 10);

    const loadData = async () => {
      let resolvedProcess: ProcessType | null = null;

      if (processes.length > 0) {
        const found = processes.find((item: ProcessType) => item.id === processId);
        if (found) {
          if (!canViewProcess(found)) {
            navigate('/procesos');
            return;
          }
          dispatch(setProcess(found));
          resolvedProcess = found;
        }
      }

      if (!resolvedProcess || resolvedProcess.id !== processId) {
        resolvedProcess = await getProcess();
      }

      if (!resolvedProcess) {
        return;
      }

      if (processes.length === 0) {
        await getProcesses();
      }

      // get all orders process for process.id
      await getOrdersProcess();
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!process) {
    return <LoaderTemporal />;
  }

  const hoyOnlyDate = new Date();
  hoyOnlyDate.setHours(0, 0, 0, 0);

  const conditionDecorator = {
    operator: '<',
    value: hoyOnlyDate,
    class_decorator: 'text-danger',
    type: 'date',
    limit: constants.settings.limitDateDays,
  };

  const columns: Array<ColumnsType> = [
    { title: '# Proceso', field: 'id', cell_class: 'text-center' },
    { title: 'Orden #', field: 'order_id' },
    { title: 'Cliente', field: 'customer_name' },
    { title: 'Línea', field: 'line_name' },
    { title: 'Fecha', field: 'order_date' },
    { title: 'Cons Cant.', field: 'consecutives_count', cell_class: 'text-center' },
    { title: 'Cons #', field: 'consecutiveId', cell_class: 'text-center' },
    { title: 'Repo #', field: 'repositionId', cell_class: 'text-center' },
    {
      title: 'Fin Producción',
      field: 'production_date_end',
      type: 'conditional_decorator',
      decorator: conditionDecorator,
      cell_class: 'text-center',
    },
    {
      title: 'Entrega',
      field: 'delivery_date',
      type: 'conditional_decorator',
      decorator: conditionDecorator,
      cell_class: 'text-center',
    },
    { title: 'Inicio ' + process.process_name, field: 'created_at', type: 'datetime' },
    {
      title: 'Estado',
      field: 'process_status',
      type: 'decorator',
      decorator: { 'En Proceso': 'primary', Finalizado: 'success', Bloqueado: 'warning', Creada: 'info' },
    },
  ];

  const handleStartOrderProcess = (orderProcess: OrderProcessType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea comenzar este proceso?',
        alertType: 'danger',
        okFunc: () => startOrderProcess(orderProcess.id),
      })
    );
  };

  const startOrderProcess = async (processOrder_id: number) => {
    dispatch(setLoading(true));
    const data = { process_status: constants.processStatus.EN_PROCESO };
    const response = await apiCall(
      'PUT',
      `order_process/${processOrder_id}/change/`,
      data,
      'Proceso Iniciado',
      'Error al iniciar el proceso',
      true
    );
    dispatch(setLoading(false));
    if (response) {
      getOrdersProcess();
    }
  };

  const handleFinishOrderProcess = (orderProcess: OrderProcessType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea finalizar este proceso?',
        alertType: 'danger',
        okFunc: () => finishOrderProcess(orderProcess.id),
      })
    );
  };

  const finishOrderProcess = async (processOrder_id: number) => {
    dispatch(setLoading(true));
    const data = { process_status: constants.processStatus.FINALIZADO };
    const response = await apiCall(
      'PUT',
      `order_process/${processOrder_id}/change/`,
      data,
      'Proceso finalizado',
      'Error al finalizar el proceso',
      true
    );
    dispatch(setLoading(false));
    if (response) {
      getOrdersProcess();
    }
  };

  const handleBlockOrderProcess = async (processOrder: any) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea bloquear o pausar este proceso?',
        alertType: 'danger',
        okFunc: () => blockOrderProcess(processOrder.id),
        input: ' ',
        inputPlaceholder: 'Escriba una razón para bloquear o pausar el proceso: ',
      })
    );
  };

  const blockOrderProcess = async (processOrder_id: number) => {
    const ppconf = store.getState().misc.popConfirmation;
    if (ppconf.input === '' || ppconf.input === undefined || ppconf.input === null || ppconf.input === ' ') {
      dispatch(
        setPopAlert({
          message: 'Debe escribir una razón para bloquear o pausar el proceso',
          show: true,
          alertType: 'danger',
        })
      );
      return;
    }

    dispatch(setLoading(true));
    const data = { process_status: constants.processStatus.BLOQUEADO, process_comment: ppconf.input };
    const response = await apiCall(
      'PUT',
      `order_process/${processOrder_id}/change/`,
      data,
      'Proceso bloqueado o pausado',
      'Error al bloquear o pausar el proceso',
      true
    );
    dispatch(setLoading(false));
    if (response) {
      dispatch(setPopConfirmation({ show: false, message: '', alertType: 'info', input: null }));
      const newOrdersProcess = ordersProcess.map((op: OrderProcessType) =>
        op.id === processOrder_id ? { ...op, process_status: constants.processStatus.BLOQUEADO } : op
      );
      setOrdersProcess(newOrdersProcess);
    }
  };

  const handleResumeOrderProcess = async (processOrder: any) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea reanudar este proceso?',
        alertType: 'danger',
        okFunc: () => resumeOrderProcess(processOrder.id),
      })
    );
  };

  const resumeOrderProcess = async (processOrder_id: number) => {
    dispatch(setLoading(true));
    const data = { process_status: constants.processStatus.EN_PROCESO };
    const response = await apiCall(
      'PUT',
      `order_process/${processOrder_id}/change/`,
      data,
      'Proceso reanudado',
      'Error al reanudar el proceso',
      true
    );
    dispatch(setLoading(false));
    if (response) {
      const newOrdersProcess = ordersProcess.map((op: OrderProcessType) =>
        op.id === processOrder_id ? { ...op, process_status: constants.processStatus.EN_PROCESO } : op
      );
      setOrdersProcess(newOrdersProcess);
    }
  };

  const handleViewOrder = (orderProcess: any) => {
    navigate(`/orden/${orderProcess.order_id}/detalle`);
  };

  const getRouteProcessOptions = async (processOrderId: number) => {
    const response = await apiCall(
      'GET',
      `order_process/${processOrderId}/route/`,
      null,
      '',
      'No se pudo obtener los procesos de la ruta',
      true
    );

    const rawList = Array.isArray(response)
      ? response
      : Array.isArray(response?.route)
        ? response.route
        : Array.isArray(response?.processes)
          ? response.processes
          : Array.isArray(response?.results)
            ? response.results
            : [];

    return rawList
      .map((item: any) => {
        const idValue = item?.id ?? item?.process_id ?? item?.process?.id ?? item?.value;
        const labelValue = item?.process_name ?? item?.name ?? item?.process?.process_name ?? item?.label;
        if (!idValue || !labelValue) return null;
        return { value: idValue, label: labelValue };
      })
      .filter(Boolean) as Array<{ value: number; label: string }>;
  };

  const handleMoveToProcess = async (processOrder: any) => {
    const routeOptions = await getRouteProcessOptions(processOrder.id);
    if (routeOptions.length === 0) {
      dispatch(
        setPopAlert({
          message: 'No hay procesos disponibles para mover esta orden',
          show: true,
          alertType: 'info',
        })
      );
      return;
    }

    const defaultProcessValue =
      routeOptions.find((option) => option.value === process.id)?.value ?? routeOptions[0].value;
    const inputData = [
      {
        label: 'Proceso al que desea mover la orden:',
        name: 'process',
        type: 'select',
        options: routeOptions,
        value: defaultProcessValue,
      },
      {
        label: 'Razón para mover la orden:',
        name: 'reason',
        type: 'text',
        value: '',
      },
    ];

    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea mover este proceso?',
        alertType: 'danger',
        okFunc: () => moveOrderProcess(processOrder.id),
        inputData: inputData,
        inputPlaceholder: '',
      })
    );
  };

  const moveOrderProcess = async (processOrder_id: number) => {
    const ppconf = store.getState().misc.popConfirmation;
    if (
      ppconf.inputData[0].value === '' ||
      ppconf.inputData[0].value === undefined ||
      ppconf.inputData[0].value === null ||
      ppconf.inputData[0].value === ' '
    ) {
      dispatch(
        setPopAlert({ message: 'Debe seleccionar un proceso al que mover la orden', show: true, alertType: 'danger' })
      );
      return;
    }

    if (
      ppconf.inputData[1].value === '' ||
      ppconf.inputData[1].value === undefined ||
      ppconf.inputData[1].value === null ||
      ppconf.inputData[1].value === ' '
    ) {
      dispatch(
        setPopAlert({ message: 'Debe escribir una razón para mover la orden', show: true, alertType: 'danger' })
      );
      return;
    }

    if (ppconf.inputData[0].value === process.id) {
      dispatch(setPopAlert({ message: 'La orden ya se encuentra en este proceso', show: true, alertType: 'info' }));
      return;
    }

    dispatch(setLoading(true));
    const data = { move_to: ppconf.inputData[0].value, reason: ppconf.inputData[1].value };
    const response = await apiCall(
      'PATCH',
      `order_process/${processOrder_id}/move/`,
      data,
      'Proceso movido',
      'Error al mover el proceso',
      true
    );
    dispatch(setLoading(false));
    if (response) {
      dispatch(setPopConfirmation({ show: false, message: '', alertType: 'info', input: null }));
      const newOrdersProcess = ordersProcess.filter((op: OrderProcessType) => op.id !== processOrder_id);
      setOrdersProcess(newOrdersProcess);
    }
  };

  const handleReportReposition = async (processOrder: any) => {
    const routeOptions = await getRouteProcessOptions(processOrder.id);
    if (routeOptions.length === 0) {
      dispatch(
        setPopAlert({
          message: 'No hay procesos disponibles para reportar la reposición',
          show: true,
          alertType: 'info',
        })
      );
      return;
    }

    const defaultProcessValue =
      routeOptions.find((option) => option.value === process.id)?.value ?? routeOptions[0].value;
    const inputData = [
      {
        label: '¿Dónde cree que se generó el daño?',
        name: 'process',
        type: 'select',
        options: routeOptions,
        value: defaultProcessValue,
      },
      {
        label: 'Razón del reporte',
        name: 'reason',
        type: 'textarea',
        value: '',
        rows: 5,
      },
      {
        label: 'Adjunte evidencia (opcional)',
        name: 'evidence',
        type: 'file',
        value: [],
        multiple: true,
        accept: 'image/*',
      },
    ];

    dispatch(
      setPopConfirmation({
        show: true,
        message: 'Complete la información para reportar reposición',
        alertType: 'danger',
        okFunc: () => reportReposition(processOrder.id),
        okText: 'Generar reporte de reposición',
        cancelText: 'Cancelar',
        inputData: inputData,
        inputPlaceholder: '',
      })
    );
  };

  const reportReposition = async (processOrder_id: number) => {
    const ppconf = store.getState().misc.popConfirmation;
    const selectedProcess = ppconf.inputData?.[0]?.value;
    const reason = ppconf.inputData?.[1]?.value;
    const evidenceFiles = ppconf.inputData?.[2]?.value || [];

    if (!selectedProcess) {
      dispatch(
        setPopAlert({ message: 'Debe seleccionar el proceso donde se generó el daño', show: true, alertType: 'danger' })
      );
      return;
    }

    if (!reason || reason.trim() === '') {
      dispatch(setPopAlert({ message: 'Debe escribir una razón del reporte', show: true, alertType: 'danger' }));
      return;
    }

    dispatch(setLoading(true));
    const formData = new FormData();
    formData.append('order_process', processOrder_id.toString());
    formData.append('process_origin', selectedProcess.toString());
    formData.append('reason', reason);
    evidenceFiles.forEach((file: File) => formData.append('evidences', file));

    const response = await apiCall(
      'POST',
      'repositions/',
      formData,
      'Reporte de reposición generado',
      'Error al generar el reporte de reposición',
      { isFormData: true }
    );
    dispatch(setLoading(false));
    if (response) {
      dispatch(setPopConfirmation({ show: false, message: '', alertType: 'info', input: null, inputData: null }));
      getOrdersProcess();
    }
  };

  const canViewOrderDetail = canView_orders || canView_orders_all || canView_orders_own || canView_orderprocess;

  const actions = [
    canViewOrderDetail ? { icon: <FontAwesomeIcon icon={faEye} />, tooltip: 'Ver', onClick: handleViewOrder } : null,
    canChange_orderprocess
      ? {
          icon: <FontAwesomeIcon icon={faPlay} />,
          tooltip: 'Iniciar',
          onClick: handleStartOrderProcess,
          condition: { field: 'process_status', value: constants.processStatus.POR_INICIAR, operator: '===' },
        }
      : null,
    canChange_orderprocess
      ? {
          icon: <FontAwesomeIcon icon={faCheck} />,
          tooltip: 'Finalizar',
          onClick: handleFinishOrderProcess,
          condition: { field: 'process_status', value: constants.processStatus.EN_PROCESO, operator: '===' },
        }
      : null,
    canChange_orderprocess
      ? {
          icon: <FontAwesomeIcon icon={faBan} />,
          tooltip: 'Bloquear/Pausar',
          onClick: handleBlockOrderProcess,
          condition: { field: 'process_status', value: constants.processStatus.EN_PROCESO, operator: '===' },
        }
      : null,
    canChange_orderprocess
      ? {
          icon: <FontAwesomeIcon icon={faForward} />,
          tooltip: 'Reanudar',
          onClick: handleResumeOrderProcess,
          condition: { field: 'process_status', value: constants.processStatus.BLOQUEADO, operator: '===' },
        }
      : null,
    canAdd_orderprocess
      ? { icon: <FontAwesomeIcon icon={faRoute} />, tooltip: 'Mover a otro proceso', onClick: handleMoveToProcess }
      : null,
    canAdd_reposition
      ? {
          icon: <FontAwesomeIcon icon={faTriangleExclamation} />,
          tooltip: 'Reportar reposición',
          onClick: handleReportReposition,
        }
      : null,
  ];

  return (
    <div className='container-wrap'>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10} className='text-left'>
          <h2 className='text-left'>{process.process_name}</h2>
        </Grid>
        <Grid item xs={12} sm={2} className='text-right'>
          <Button className='btn btn-primary' onClick={() => navigate('/procesos/')}>
            Volver
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} key={'process'} className='text-left'>
          <div className='process-orders-wrap'>
            <DataTable data={ordersProcess} columns={columns} actions={actions} />
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default ViewOrdersProcess;
